import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE from "./apiBase";
import { formatCompactNumber } from "./numberFormatUtils";
import maskUtils from "./maskUtils";

const { maskEmail, maskPhone } = maskUtils;

const CONSENT_COLORS = {
  Marketing: { bg: "#EEF2FF", text: "#4338CA", dot: "#6366F1" },
  Analytics: { bg: "#F0FDF4", text: "#15803D", dot: "#22C55E" },
  Functional: { bg: "#FFF7ED", text: "#C2410C", dot: "#F97316" },
  "Third-Party Sharing": { bg: "#FDF4FF", text: "#7E22CE", dot: "#A855F7" },
  Notifications: { bg: "#EFF6FF", text: "#1D4ED8", dot: "#3B82F6" },
};

const FILTERS = ["All", "Active", "Pending", "Revoked", "Cancelled", "Deleted"];

export default function CustomerConsent() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState("");
  const [toast, setToast] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const showProcessingAlert = (title = "Processing request") => {
    Swal.fire({
      title,
      text: "Please wait a moment...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
      customClass: { popup: "swal-popup-custom" },
    });
  };

  const showResultAlert = async (title, text, type = "success") => {
    Swal.close();
    await Swal.fire({
      icon: type,
      title,
      text,
      timer: 2200,
      showConfirmButton: false,
      customClass: { popup: "swal-popup-custom" },
    });
  };

  const notifyConsentUpdate = (detail) => {
    try {
      const tenant_id = localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') || null;
      const payload = { ...(detail || {}), tenant_id: tenant_id ? Number(tenant_id) : null, ts: Date.now() };
      const responseEvent = new CustomEvent('consentResponse', { detail: payload });
      document.dispatchEvent(responseEvent);
      window.dispatchEvent(responseEvent);
      const updateEvent = new CustomEvent('consent:updated', { detail: payload });
      document.dispatchEvent(updateEvent);
      window.dispatchEvent(updateEvent);
      try {
        localStorage.setItem('consent:update', JSON.stringify(payload));
        localStorage.removeItem('consent:update');
      } catch (e) {
        // ignore cross-window sync errors
      }
    } catch (e) {
      // ignore
    }
  };

  const redirectToCustomerConsentLogin = () => {
    const loginPath = "/customer-consent-login";
    try {
      window.history.replaceState({}, "", loginPath);
    } catch (e) { }
    try {
      window.location.replace(loginPath);
    } catch (e) {
      window.location.href = loginPath;
    }
  };

  // ── Back button handler ──
  const handleBack = () => {
    redirectToCustomerConsentLogin();
  };

  // ── Logout button handler ──
  const handleLogout = async () => {
    try {
      localStorage.removeItem("customer_consent_email");
      localStorage.removeItem("dsar_user_email");
      localStorage.removeItem("dsar_auth");
      localStorage.removeItem("dsar_user_type");
      localStorage.removeItem("dsar_prefill");
    } catch (e) { }

    showProcessingAlert("Logging out");
    await new Promise((resolve) => setTimeout(resolve, 900));
    await showResultAlert("Logged out", "You have been signed out successfully.");
    setTimeout(() => {
      redirectToCustomerConsentLogin();
    }, 300);
  };

  useEffect(() => {
    const loadConsentRecords = async () => {
      setLoading(true);
      setError("");
      try {
        const storedEmail =
          localStorage.getItem("customer_consent_email") ||
          localStorage.getItem("dsar_user_email") ||
          "";
        const email = storedEmail.trim().toLowerCase();

        if (!email) {
          redirectToCustomerConsentLogin();
          setError("Please login through the Customer Consent login page to view your consent records.");
          setData([]);
          return;
        }

        try {
          window.history.replaceState({}, "", window.location.pathname);
        } catch (e) { }

        const res = await axios.get(`${API_BASE}/user/consent`, {
          params: { email, limit: 1000, records_only: true },
          timeout: 30000,
        });

        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data?.consents || res.data?.consent || []);
        if (!Array.isArray(rows)) {
          throw new Error("The consent service returned an invalid response.");
        }
        const records = rows.map((r) => {
            const rawStatus = String(r.status || "").toLowerCase();
            const normalizedStatus =
              rawStatus === "revoked" || rawStatus === "revoke"
                ? "Revoked"
                : rawStatus === "pending"
                  ? "Pending"
                  : rawStatus === "cancelled" || rawStatus === "canceled" || rawStatus.includes("cancel")
                    ? "Cancelled"
                    : rawStatus.includes("delet")
                      ? "Deleted"
                      : ["active", "accepted", "granted", "approved"].includes(rawStatus)
                        ? "Active"
                        : r.status || "Pending";
            return {
              id: r.id,
              name: r.username || r.email || "Unknown",
              email: r.email || email,
              phone: r.phone || "",
              address: r.locality || r.department || r.parent_name || "",
              consentType: r.type || r.app_name || "Consent",
              status: normalizedStatus,
              purpose: r.consent || r.type || "",
              datetime: r.created_at
                ? new Date(r.created_at).toLocaleString()
                : r.response_at
                  ? new Date(r.response_at).toLocaleString()
                  : "",
            };
          });

        setData(records);
      } catch (err) {
        console.error("Failed to load customer consent records", err);
        setError(err?.code === "ECONNABORTED"
          ? "Loading consent records timed out. Please try again."
          : "Unable to load consent records at this time. Please try again.");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadConsentRecords();
  }, []);

  const filtered = data.filter((r) => {
    const matchFilter = filter === "All" || r.status === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.consentType.toLowerCase().includes(q) ||
      r.phone.includes(q);
    return matchFilter && matchSearch;
  });

  const handleRevoke = async () => {
    if (!reason || !modal) return;
    showProcessingAlert("Revoking consent");
    try {
      const form = new FormData();
      form.append('consent_id', modal.id);
      form.append('reason', reason);
      form.append('create_dsar_ticket', 'true');

      const tenantId = localStorage.getItem('tenant_id') || localStorage.getItem('tenantId');
      if (tenantId) form.append('tenant_id', tenantId);

      const selectedDrivePreference = (() => {
        try {
          return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
        } catch (e) {
          return '';
        }
      })();

      const headers = {};
      if (selectedDrivePreference) {
        form.append('drive_type', selectedDrivePreference);
        headers['X-Drive-Type'] = selectedDrivePreference;
      }

      const response = await axios.post(`${API_BASE}/user/consent/revoke`, form, { headers });
      setData((d) => d.map((r) => (r.id === modal.id ? { ...r, status: 'Revoked' } : r)));
      const dsarId = response.data?.dsar_id;
      notifyConsentUpdate({ id: modal.id, dsar_id: dsarId, status: 'Revoked', action: 'revoke' });
      setModal(null);
      setReason('');
      const ticketRef = dsarId ? ` DSAR ticket DSAR-${String(dsarId).padStart(6, '0')} was created.` : '';
      await showResultAlert("Consent revoked", `Consent revoked for ${modal.name}.${ticketRef}`);
    } catch (err) {
      await showResultAlert("Revoke failed", 'Unable to revoke consent right now.', 'error');
    }
  };

  const handleCancel = async (row) => {
    if (!row) return;
    showProcessingAlert("Cancelling consent");
    try {
      const response = await axios.post(`${API_BASE}/user/consent/${row.id}/cancel`);
      const dsarId = response.data?.dsar_id;
      setData((d) => d.map((r) => (r.id === row.id ? { ...r, status: 'Cancelled' } : r)));
      notifyConsentUpdate({ id: row.id, dsar_id: dsarId, status: 'Cancelled', action: 'cancel' });
      const ticketRef = dsarId ? ` DSAR ticket DSAR-${String(dsarId).padStart(6, '0')} was created.` : '';
      await showResultAlert("Consent cancelled", `Consent cancelled for ${row.name}.${ticketRef}`);
    } catch (err) {
      await showResultAlert("Cancellation failed", 'Unable to cancel consent right now.', 'error');
    }
  };

  const stats = {
    total: data.length,
    active: data.filter((r) => r.status === "Active").length,
    pending: data.filter((r) => r.status === "Pending").length,
    revoked: data.filter((r) => r.status === "Revoked").length,
    cancelled: data.filter((r) => r.status === "Cancelled").length,
  };

  const activePct = stats.total ? Math.round((stats.active / stats.total) * 100) : 0;
  const revokedPct = stats.total ? Math.round((stats.revoked / stats.total) * 100) : 0;

  const customerName = data?.[0]?.name || "Customer Consent";

  const initials = (name) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <div style={s.root}>
      <main style={s.main}>

        {/* ── Page Header ── */}
        <div style={s.topBar}>
          <div style={s.topBarLeft}>
            <div>
              <div style={s.pageTitle}>{customerName}</div>
              <div style={s.pageSub}>Consent management overview</div>
            </div>
          </div>
          <div style={s.topBarRight}>
            <div style={s.liveBadge}>
              <span style={s.liveDot} />
              Live data
            </div>
            <button
              style={s.backBtn}
              onClick={handleBack}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F1F5F9"; e.currentTarget.style.borderColor = "#CBD5E1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
              title="Go back"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              Back
            </button>
            {/* <button
              style={s.linkBtn}
              onClick={() => { window.location.href = '/dsar-dashboard'; }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.borderColor = '#BFDBFE'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
              title="DSAR Dashboard"
            >
              DSAR
            </button> */}
            <button
              style={s.logoutBtn}
              onClick={handleLogout}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.borderColor = "#EF4444"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#FECACA"; }}
              title="Logout"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
              Logout
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div style={s.statsRow}>
          {/* Total */}
          <div style={s.statCard}>
            <div style={{ ...s.statAccent, background: "#6366F1" }} />
            <div style={s.statTop}>
              <div style={{ ...s.statIcon, background: "#EEF2FF", color: "#6366F1" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="2" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></svg>
              </div>
              <div style={{ ...s.statTrend, background: "#EEF2FF", color: "#4338CA" }}>↑ 12%</div>
            </div>
            <div style={{ ...s.statNum, color: "#0F172A" }} title={Number(stats.total || 0).toLocaleString()}>{formatCompactNumber(stats.total || 0)}</div>
            <div style={s.statLabel}>Total Records</div>
            <div style={s.progressWrap}>
              <div style={{ ...s.progressFill, width: "100%", background: "#6366F1" }} />
            </div>
            <div style={s.statFooter}>
              <span style={s.statFooterText}>All consent records</span>
              <span style={{ ...s.statFooterPct, color: "#6366F1" }}>100%</span>
            </div>
          </div>

          {/* Active */}
          <div style={s.statCard}>
            <div style={{ ...s.statAccent, background: "#16A34A" }} />
            <div style={s.statTop}>
              <div style={{ ...s.statIcon, background: "#F0FDF4", color: "#16A34A" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div style={{ ...s.statTrend, background: "#F0FDF4", color: "#15803D" }}>↑ 8%</div>
            </div>
            <div style={{ ...s.statNum, color: "#15803D" }} title={Number(stats.active || 0).toLocaleString()}>{formatCompactNumber(stats.active || 0)}</div>
            <div style={s.statLabel}>Active Consents</div>
            <div style={s.progressWrap}>
              <div style={{ ...s.progressFill, width: `${activePct}%`, background: "#16A34A" }} />
            </div>
            <div style={s.statFooter}>
              <span style={s.statFooterText}>of total records</span>
              <span style={{ ...s.statFooterPct, color: "#16A34A" }}>{activePct}%</span>
            </div>
          </div>

          {/* Revoked */}
          <div style={s.statCard}>
            <div style={{ ...s.statAccent, background: "#DC2626" }} />
            <div style={s.statTop}>
              <div style={{ ...s.statIcon, background: "#FEF2F2", color: "#DC2626" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              </div>
              <div style={{ ...s.statTrend, background: "#FEF2F2", color: "#B91C1C" }}>— 0%</div>
            </div>
            <div style={{ ...s.statNum, color: "#DC2626" }} title={Number(stats.revoked || 0).toLocaleString()}>{formatCompactNumber(stats.revoked || 0)}</div>
            <div style={s.statLabel}>Revoked</div>
            <div style={s.progressWrap}>
              <div style={{ ...s.progressFill, width: `${revokedPct || 0}%`, background: "#DC2626" }} />
            </div>
            <div style={s.statFooter}>
              <span style={s.statFooterText}>of total records</span>
              <span style={{ ...s.statFooterPct, color: "#DC2626" }}>{revokedPct}%</span>
            </div>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={s.errorBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            {error}
          </div>
        )}

        {/* ── Toolbar ── */}
        <div style={s.toolbar}>
          <div style={s.searchWrap}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              style={s.searchInput}
              placeholder="Search by name, email, phone or consent type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button style={s.clearBtn} onClick={() => setSearch("")}>✕</button>
            )}
          </div>
          <div style={s.filterGroup}>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* ── Table ── */}
        <div style={s.tableWrap}>
          {loading ? (
            <div style={s.loadingBox}>
              <div style={s.spinner} />
              Loading consent records…
            </div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  {["#", "Customer", "Contact", "Address", "Consent Type", "Status", "Date & Time", "Action"].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={s.empty}>
                      <div style={s.emptyIcon}>📭</div>
                      No records found
                    </td>
                  </tr>
                ) : (
                  filtered.map((row, i) => {
                    const ct = CONSENT_COLORS[row.consentType] || CONSENT_COLORS.Marketing;
                    const isActive = row.status === "Active";
                    const isPending = row.status === "Pending";
                    const isCancelled = row.status === "Cancelled";
                    return (
                      <tr
                        key={row.id}
                        style={s.tr}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ ...s.td, color: "#CBD5E1", fontWeight: 600, fontSize: 12 }}>{String(i + 1).padStart(2, "0")}</td>
                        <td style={s.td}>
                          <div style={s.customerCell}>
                            <div style={{ ...s.avatar, background: ct.bg, color: ct.text }}>
                              {initials(row.name)}
                            </div>
                            <div>
                              <div style={s.custName} onClick={() => setDetailRow(row)}>{row.name}</div>
                              <div style={s.custEmail}>{maskEmail(row.email)}</div>
                            </div>
                          </div>
                        </td>
                        <td style={s.td}>
                          <div style={s.phone}>{maskPhone(row.phone)}</div>
                        </td>
                        <td style={{ ...s.td, maxWidth: 160 }}>
                          <div style={s.address}>{row.address}</div>
                        </td>
                        <td style={s.td}>
                          <span style={{ ...s.badge, background: ct.bg, color: ct.text }}>
                            <span style={{ ...s.dot, background: ct.dot }} />
                            {row.consentType}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{
                            ...s.statusBadge,
                            background: isActive ? "#F0FDF4" : "#FEF2F2",
                            color: isActive ? "#15803D" : "#DC2626",
                          }}>
                            <span style={{
                              ...s.statusDot,
                              background: isActive ? "#22C55E" : "#EF4444",
                              animation: isActive ? "pulse 1.8s infinite" : "none",
                            }} />
                            {row.status}
                          </span>
                        </td>
                        <td style={{ ...s.td, fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                          {row.datetime}
                        </td>
                        <td style={s.td}>
                          {isActive ? (
                            <button
                              style={s.revokeBtn}
                              onClick={() => setModal(row)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#FEE2E2";
                                e.currentTarget.style.borderColor = "#EF4444";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.borderColor = "#FECACA";
                              }}
                            >
                              ✕ Revoke
                            </button>
                          ) : isPending ? (
                            <button
                              style={s.cancelBtn}
                              onClick={() => handleCancel(row)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#FEE2E2";
                                e.currentTarget.style.borderColor = "#EF4444";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.borderColor = "#FECACA";
                              }}
                            >
                              ✕ Cancel
                            </button>
                          ) : isCancelled ? (
                            <span style={s.revokedTag}>Cancelled</span>
                          ) : (
                            <span style={s.revokedTag}>{row.status}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={s.tableFooter}>
          Showing <strong title={Number(filtered.length).toLocaleString()}>{formatCompactNumber(filtered.length)}</strong> of <strong title={Number(data.length).toLocaleString()}>{formatCompactNumber(data.length)}</strong> records
        </div>
      </main>

      {/* ── Revoke Modal ── */}
      {modal && (
        <div style={s.overlay} onClick={() => { setModal(null); setReason(""); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <button
              style={s.modalCloseBtn}
              onClick={() => { setModal(null); setReason(""); }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "#FECACA"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.color = "#94A3B8"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
            >✕</button>

            <div style={s.modalIconWrap}>
              <div style={s.modalIcon}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
              </div>
            </div>

            <div style={s.modalTitle}>Revoke Consent</div>
            <div style={s.modalSub}>
              You are about to revoke consent for this customer.{" "}
              <strong>This action cannot be undone.</strong>
            </div>

            <div style={s.modalUserCard}>
              <div style={{
                ...s.modalUserAvatar,
                background: (CONSENT_COLORS[modal.consentType] || CONSENT_COLORS.Marketing).bg,
                color: (CONSENT_COLORS[modal.consentType] || CONSENT_COLORS.Marketing).text,
              }}>
                {initials(modal.name)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={s.modalUserName}>{modal.name}</div>
                <div style={s.modalUserEmail}>{maskEmail(modal.email)}</div>
                <span style={{
                  ...s.modalConsentPill,
                  background: (CONSENT_COLORS[modal.consentType] || CONSENT_COLORS.Marketing).bg,
                  color: (CONSENT_COLORS[modal.consentType] || CONSENT_COLORS.Marketing).text,
                }}>
                  <span style={{ ...s.dot, background: (CONSENT_COLORS[modal.consentType] || CONSENT_COLORS.Marketing).dot }} />
                  {modal.consentType}
                </span>
              </div>
            </div>

            <div style={s.modalDivider} />

            <div style={s.modalInfo}>
              <div style={s.modalInfoRow}>
                <span style={s.infoLabel}>Purpose</span>
                <span style={s.infoValue}>{modal.purpose}</span>
              </div>
              <div style={{ ...s.modalInfoRow, marginTop: 6 }}>
                <span style={s.infoLabel}>Granted on</span>
                <span style={s.infoValue}>{modal.datetime}</span>
              </div>
            </div>

            <label style={s.modalLabel}>Reason for revocation *</label>
            <select
              style={s.select}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Select a reason…</option>
              <option>User requested withdrawal</option>
              <option>No longer required</option>
              <option>Privacy concern raised</option>
              <option>Account deletion initiated</option>
              <option>Regulatory compliance</option>
            </select>

            <div style={s.modalActions}>
              <button
                style={s.cancelBtn}
                onClick={() => { setModal(null); setReason(""); }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >Cancel</button>
              <button
                style={{ ...s.confirmBtn, opacity: reason ? 1 : 0.45, cursor: reason ? "pointer" : "not-allowed" }}
                disabled={!reason}
                onClick={handleRevoke}
                onMouseEnter={(e) => { if (reason) e.currentTarget.style.background = "#B91C1C"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#DC2626"; }}
              >Confirm Revoke</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Drawer ── */}
      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={s.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={s.drawerHeader}>
              <div style={s.drawerTitle}>Consent Details</div>
              <button style={s.drawerClose} onClick={() => setDetailRow(null)}>✕</button>
            </div>
            <div style={s.drawerAvatar}>{initials(detailRow.name)}</div>
            <div style={s.drawerName}>{detailRow.name}</div>
            <div style={s.drawerEmail}>{maskEmail(detailRow.email)}</div>
            <div style={s.drawerDivider} />
            {[
              ["Phone", maskPhone(detailRow.phone)],
              ["Address", detailRow.address],
              ["Consent Type", detailRow.consentType],
              ["Purpose", detailRow.purpose],
              ["Status", detailRow.status],
              ["Date & Time", detailRow.datetime],
            ].map(([k, v]) => (
              <div key={k} style={s.drawerRow}>
                <div style={s.drawerKey}>{k}</div>
                <div style={s.drawerVal}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{ ...s.toast, background: toast.type === "success" ? "#16A34A" : "#DC2626" }}>
          {toast.type === "success" ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes spin { to{transform:rotate(360deg)} }
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Inter','Segoe UI',system-ui,sans-serif}
      `}</style>
    </div>
  );
}

const s = {
  root: {
    display: "flex", minHeight: "100vh", background: "#F1F5F9",
    fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
  },
  main: { flex: 1, padding: "28px 32px", overflowY: "auto", minWidth: 0 },

  // Header
  topBar: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22,
  },
  topBarLeft: { display: "flex", alignItems: "flex-start", gap: 14 },
  topBarRight: { display: "flex", alignItems: "center", gap: 10 },
  backBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 14px",
    border: "1px solid #E2E8F0", borderRadius: 8,
    background: "#fff", color: "#475569", fontSize: 12.5, fontWeight: 600,
    cursor: "pointer", transition: "all 0.2s",
  },
  logoutBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 14px", border: "1px solid #FECACA", borderRadius: 8,
    background: "transparent", color: "#DC2626", fontSize: 12.5, fontWeight: 600,
    cursor: "pointer", transition: "all 0.2s",
  },
  pageTitle: { fontSize: 22, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.4px" },
  pageSub: { fontSize: 12.5, color: "#94A3B8", marginTop: 3 },
  liveBadge: {
    display: "flex", alignItems: "center", gap: 6,
    background: "#EEF2FF", color: "#4338CA",
    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    border: "1px solid #C7D2FE",
  },
  liveDot: {
    width: 7, height: 7, borderRadius: "50%", background: "#6366F1",
    animation: "pulse 1.8s infinite", flexShrink: 0,
  },

  // Stats
  statsRow: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 22 },
  statCard: {
    background: "#fff", borderRadius: 16, padding: "20px 22px",
    border: "1px solid #E8EDF4", position: "relative", overflow: "hidden",
  },
  statAccent: { position: "absolute", top: 0, left: 0, right: 0, height: 3, borderRadius: "16px 16px 0 0" },
  statTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 },
  statIcon: {
    width: 42, height: 42, borderRadius: 11,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statTrend: {
    display: "flex", alignItems: "center", gap: 3,
    fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
  },
  statNum: { fontSize: 34, fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1, marginBottom: 4 },
  statLabel: { fontSize: 12, color: "#94A3B8", fontWeight: 500, marginBottom: 14 },
  progressWrap: { background: "#F1F5F9", borderRadius: 6, height: 5, overflow: "hidden", marginBottom: 14 },
  progressFill: { height: "100%", borderRadius: 6, transition: "width 0.8s cubic-bezier(.4,0,.2,1)" },
  statFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #F1F5F9" },
  statFooterText: { fontSize: 11.5, color: "#94A3B8" },
  statFooterPct: { fontSize: 12, fontWeight: 700 },

  // Error
  errorBox: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10,
    padding: "12px 16px", fontSize: 13, color: "#DC2626", marginBottom: 16,
  },

  // Toolbar
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12 },
  searchWrap: {
    flex: 1, display: "flex", alignItems: "center", gap: 8,
    background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
    padding: "0 14px", height: 42,
  },
  searchInput: { flex: 1, border: "none", outline: "none", fontSize: 13.5, color: "#334155", background: "transparent" },
  clearBtn: { background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 13, padding: 2 },
  filterGroup: { display: "flex", gap: 6 },
  filterBtn: {
    padding: "8px 16px", border: "1px solid #E2E8F0", borderRadius: 8,
    background: "#fff", fontSize: 13, color: "#64748B", cursor: "pointer", fontWeight: 500,
  },
  filterActive: { background: "#EEF2FF", color: "#4338CA", borderColor: "#C7D2FE", fontWeight: 600 },

  // Table
  tableWrap: { background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
  thead: { background: "#F8FAFC" },
  th: {
    padding: "12px 14px", textAlign: "left", fontWeight: 600, fontSize: 11.5,
    color: "#64748B", letterSpacing: "0.04em", textTransform: "uppercase",
    borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #F1F5F9", transition: "background 0.15s", cursor: "default" },
  td: { padding: "13px 14px", verticalAlign: "middle", color: "#334155" },
  empty: { padding: "56px", textAlign: "center", color: "#94A3B8", fontSize: 14 },
  emptyIcon: { fontSize: 32, marginBottom: 8 },
  loadingBox: {
    padding: "48px", display: "flex", flexDirection: "column",
    alignItems: "center", gap: 14, color: "#94A3B8", fontSize: 13.5,
  },
  spinner: {
    width: 28, height: 28, border: "3px solid #E2E8F0",
    borderTop: "3px solid #6366F1", borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  customerCell: { display: "flex", alignItems: "center", gap: 10 },
  avatar: { width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 },
  custName: { fontSize: 13.5, fontWeight: 600, color: "#1E293B", cursor: "pointer" },
  custEmail: { fontSize: 11.5, color: "#94A3B8", marginTop: 1 },
  phone: { fontSize: 13, color: "#475569", whiteSpace: "nowrap" },
  address: { fontSize: 12, color: "#64748B", lineHeight: 1.5, maxWidth: 150 },
  badge: { display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  dot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  statusBadge: { display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  statusDot: { width: 6, height: 6, borderRadius: "50%" },
  revokeBtn: {
    padding: "5px 12px", border: "1px solid #FECACA", borderRadius: 7,
    background: "transparent", color: "#DC2626", fontSize: 12, fontWeight: 600,
    cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap",
  },
  revokedTag: { fontSize: 11.5, color: "#94A3B8", background: "#F8FAFC", padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0" },
  tableFooter: { padding: "12px 16px", fontSize: 12, color: "#94A3B8", textAlign: "right" },

  // Modal overlay
  overlay: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: "#fff", borderRadius: 20, padding: "36px 32px 32px",
    width: "100%", maxWidth: 460,
    boxShadow: "0 20px 60px rgba(15,23,42,0.2)",
    position: "relative", maxHeight: "90vh", overflowY: "auto",
  },
  modalCloseBtn: {
    position: "absolute", top: 14, right: 14,
    width: 32, height: 32, borderRadius: "50%",
    background: "#F8FAFC", border: "1px solid #E2E8F0",
    color: "#94A3B8", fontSize: 14, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    lineHeight: 1, transition: "all 0.2s",
  },
  modalIconWrap: { display: "flex", justifyContent: "center", marginBottom: 16 },
  modalIcon: {
    width: 54, height: 54, borderRadius: "50%",
    background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center",
    border: "2px solid #FECACA",
  },
  modalTitle: { fontSize: 20, fontWeight: 700, color: "#0F172A", textAlign: "center", marginBottom: 8 },
  modalSub: { fontSize: 13.5, color: "#64748B", textAlign: "center", lineHeight: 1.65, marginBottom: 20 },
  modalUserCard: {
    display: "flex", alignItems: "center", gap: 14,
    background: "#F8FAFC", borderRadius: 12, padding: "14px 16px",
    marginBottom: 16, border: "1px solid #F1F5F9",
  },
  modalUserAvatar: { width: 46, height: 46, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
  modalUserName: { fontSize: 14, fontWeight: 600, color: "#1E293B" },
  modalUserEmail: { fontSize: 12, color: "#94A3B8", marginTop: 2, marginBottom: 6 },
  modalConsentPill: { display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 },
  modalDivider: { height: 1, background: "#F1F5F9", margin: "4px 0 16px" },
  modalInfo: { background: "#F8FAFC", borderRadius: 10, padding: "12px 16px", marginBottom: 18, border: "1px solid #F1F5F9" },
  modalInfoRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 12.5, color: "#334155", padding: "3px 0", gap: 12 },
  infoLabel: { color: "#94A3B8", fontWeight: 500, flexShrink: 0 },
  infoValue: { color: "#334155", fontSize: 12.5, textAlign: "right" },
  modalLabel: { fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 },
  select: {
    width: "100%", height: 46, borderRadius: 10, border: "1.5px solid #E2E8F0",
    padding: "0 36px 0 14px", fontSize: 13.5, color: "#334155", background: "#fff",
    outline: "none", marginBottom: 22, cursor: "pointer", appearance: "none",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2394A3B8' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
  },
  modalActions: { display: "flex", gap: 10 },
  cancelBtn: { flex: 1, height: 46, border: "1.5px solid #E2E8F0", borderRadius: 10, background: "#fff", color: "#64748B", fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "background 0.15s" },
  confirmBtn: { flex: 1, height: 46, border: "none", borderRadius: 10, background: "#DC2626", color: "#fff", fontSize: 14, fontWeight: 600, transition: "background 0.2s" },

  // Drawer
  drawer: { background: "#fff", borderRadius: 16, padding: "28px 24px", width: 360, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", maxHeight: "85vh", overflowY: "auto" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  drawerTitle: { fontSize: 16, fontWeight: 700, color: "#0F172A" },
  drawerClose: { background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#94A3B8" },
  drawerAvatar: { width: 60, height: 60, borderRadius: "50%", background: "#EEF2FF", color: "#4338CA", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, margin: "0 auto 10px" },
  drawerName: { textAlign: "center", fontSize: 16, fontWeight: 700, color: "#0F172A" },
  drawerEmail: { textAlign: "center", fontSize: 12.5, color: "#94A3B8", marginTop: 3 },
  drawerDivider: { height: 1, background: "#F1F5F9", margin: "18px 0" },
  drawerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #F8FAFC" },
  drawerKey: { fontSize: 12, color: "#94A3B8", fontWeight: 500, flexShrink: 0, marginRight: 12 },
  drawerVal: { fontSize: 13, color: "#334155", textAlign: "right", fontWeight: 500 },

  // Toast
  toast: { position: "fixed", bottom: 24, right: 24, color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 13.5, fontWeight: 500, zIndex: 2000, boxShadow: "0 8px 24px rgba(0,0,0,0.15)" },
};